import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, Home, Plane } from 'lucide-react';
import { formatDateWithWeekday, formatTime } from '../utils/dateTime';

type LineupEntry = {
  order: number;
  number: number | null;
  name: string;
  player_id: string;
};

type FieldingEntry = {
  inning: number;
  position: string;
  player_id: string | null;
  player_name: string | null;
  player_number: number | null;
};

type GameData = {
  date: string;
  game_time: string | null;
  location: string | null;
  opponent_name: string;
  home_away: 'home' | 'away';
  our_score: number | null;
  opponent_score: number | null;
};

type ShareLineupResponse = {
  game: GameData;
  team_name: string;
  lineup: LineupEntry[];
  fielding: FieldingEntry[];
};

const ShareLineup: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ShareLineupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    void (async () => {
      try {
        const res = await fetch(
          `${supabaseUrl}/functions/v1/share-lineup?token=${encodeURIComponent(token)}`,
          { headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string } },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(json?.error || 'Failed to load lineup');
        } else {
          setData(json as ShareLineupResponse);
        }
      } catch {
        setError('Failed to load lineup');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Build fielding grid: rows = batting order players, cols = sorted innings
  const fieldingGrid = useMemo(() => {
    if (!data) return null;
    const { lineup, fielding } = data;
    if (!fielding.length) return null;

    const innings = [...new Set(fielding.map((f) => f.inning))].sort((a, b) => a - b);

    // Map: player_id → { inning → position }
    const playerInningPos: Record<string, Record<number, string>> = {};
    for (const f of fielding) {
      if (!f.player_id) continue;
      if (!playerInningPos[f.player_id]) playerInningPos[f.player_id] = {};
      playerInningPos[f.player_id][f.inning] = f.position;
    }

    // Rows: only batting-order players — exclude bench/non-lineup players entirely
    const rows = lineup.map((l) => ({ id: l.player_id, name: l.name, number: l.number, order: l.order }));

    return { innings, rows, playerInningPos };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="text-lg font-semibold text-gray-700">
          {error === 'Link revoked'
            ? 'This link has been revoked.'
            : error === 'Link expired'
            ? 'This link has expired.'
            : 'Game sheet not found.'}
        </p>
        <p className="text-sm text-gray-500">The coach may need to generate a new share link.</p>
      </div>
    );
  }

  const { game, team_name, lineup } = data;

  const scoreDisplay =
    game.our_score != null && game.opponent_score != null
      ? game.our_score > game.opponent_score
        ? { label: 'W', color: 'text-green-600', score: `${game.our_score}–${game.opponent_score}` }
        : game.our_score < game.opponent_score
        ? { label: 'L', color: 'text-red-500', score: `${game.our_score}–${game.opponent_score}` }
        : { label: 'T', color: 'text-gray-500', score: `${game.our_score}–${game.opponent_score}` }
      : null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-4">

        {/* Game header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="min-w-0">
              <h1 className="text-lg font-bold text-gray-900 leading-tight">{team_name}</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {game.home_away === 'home' ? 'vs.' : '@'} {game.opponent_name}
              </p>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              {game.home_away === 'home' ? (
                <Home className="w-4 h-4 text-primary" />
              ) : (
                <Plane className="w-4 h-4 text-gray-400" />
              )}
              {scoreDisplay && (
                <span className={`text-sm font-bold ${scoreDisplay.color}`}>
                  {scoreDisplay.label} {scoreDisplay.score}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span>{formatDateWithWeekday(game.date)}</span>
            </div>
            {game.game_time && (
              <div className="flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span>{formatTime(game.game_time)}</span>
              </div>
            )}
            {game.location && (
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span>{game.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* Fielding grid */}
        {fieldingGrid && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Lineup Card</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse min-w-max">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {/* Frozen header — z-20 so it sits above the body sticky cells */}
                    <th className="text-left px-3 py-2 font-semibold text-gray-600 border-r border-gray-200 whitespace-nowrap sticky left-0 bg-gray-50 z-20">
                      Player
                    </th>
                    {fieldingGrid.innings.map((inn) => (
                      <th
                        key={inn}
                        className="px-3 py-2 font-semibold text-gray-600 text-center border-r border-gray-200 whitespace-nowrap w-10"
                      >
                        {inn}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fieldingGrid.rows.map((row, idx) => {
                    const posMap = fieldingGrid.playerInningPos[row.id] ?? {};
                    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                    return (
                      <tr key={row.id} className={`border-b border-gray-100 ${rowBg}`}>
                        {/* Frozen player column — solid opaque background, z-10 */}
                        <td className={`px-3 py-2 border-r border-gray-200 sticky left-0 z-10 ${rowBg}`}>
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            {row.order != null && (
                              <span className="text-gray-400 tabular-nums w-4 text-right shrink-0">{row.order}.</span>
                            )}
                            {row.number != null && (
                              <span className="font-bold text-primary tabular-nums shrink-0">#{row.number}</span>
                            )}
                            <span className="font-medium text-gray-900">{row.name}</span>
                          </div>
                        </td>
                        {fieldingGrid.innings.map((inn) => {
                          const pos = posMap[inn];
                          const isSit = !pos;
                          return (
                            <td
                              key={inn}
                              className="px-2 py-1.5 text-center border-r border-gray-200 font-semibold tabular-nums whitespace-nowrap"
                            >
                              {isSit ? (
                                <span className="inline-block w-5 h-5 rounded bg-red-500" aria-label="Sitting" />
                              ) : (
                                <span className="text-gray-800">{pos}</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Fallback batting order when no fielding data exists */}
        {!fieldingGrid && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Batting Order</h2>
            </div>
            {lineup.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">No batting order set yet.</p>
            ) : (
              <ol className="divide-y divide-gray-100">
                {lineup.map((entry, idx) => (
                  <li
                    key={entry.order}
                    className={`flex items-center gap-3 px-4 py-3 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                  >
                    <span className="w-6 text-center text-sm font-bold text-gray-400 tabular-nums shrink-0">
                      {entry.order}
                    </span>
                    {entry.number != null && (
                      <span className="text-sm font-bold text-primary min-w-[2.5rem] tabular-nums">
                        #{entry.number}
                      </span>
                    )}
                    <span className="text-sm font-medium text-gray-900">{entry.name}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-2">
          Shared via HUSTLi · hustli.app
        </p>
      </div>
    </div>
  );
};

export default ShareLineup;
